/**
 * The /cost-dashboard search-param schema -- the single source of truth for
 * the page's URL state. The route's validateSearch points here, and the
 * patch type is derived from the schema so every search write stays inside
 * it: adding or removing a param is a single edit.
 */
import type { CostIsoDay } from 'pages/costDashboard/types'
import {
  COST_BREAKDOWN_GROUPS,
  COST_CHART_VIEWS,
  COST_CONFIG_STATUSES,
  COST_DASHBOARD_TABS,
  COST_GRANULARITIES,
  COST_STACK_BYS,
  COST_USAGE_AUDIENCES,
} from 'pages/costDashboard/types'
import { z } from 'zod'

const costIsoDay: z.ZodType<CostIsoDay> = z.string()

/**
 * Multi-value filter param: emits a non-empty array or undefined. A plain
 * value is accepted as a one-element set so pre-facets deep links
 * (`site=abc`) keep working; invalid values fall back to unfiltered.
 */
const multiParam = <T extends z.ZodTypeAny>(value: T) =>
  z
    .union([value, z.array(value)])
    .optional()
    .transform((raw) => {
      if (raw === undefined) return undefined
      const values = (Array.isArray(raw) ? raw : [raw]) as z.infer<T>[]
      return values.length === 0 ? undefined : values
    })
    .catch(undefined)

// Enums use catch + default so hand-edited or stale deep links fall back to
// the default instead of failing validation.
export const costDashboardSearchSchema = z.object({
  org: multiParam(z.string()),
  site: multiParam(z.string()),
  start: costIsoDay.optional(),
  end: costIsoDay.optional(),
  granularity: z.enum(COST_GRANULARITIES).catch('month').default('month'),
  view: z.enum(COST_CHART_VIEWS).catch('trend').default('trend'),
  stack_by: z.enum(COST_STACK_BYS).catch('category').default('category'),
  category: multiParam(z.string()),
  service: multiParam(z.string()),
  project: multiParam(z.string()),
  config: multiParam(z.string()),
  config_status: multiParam(z.enum(COST_CONFIG_STATUSES)),
  // Stale deep links to the retired costs/sites/models tabs land on 'users'
  // (their tables live under the Costs card's group toggle now).
  tab: z.enum(COST_DASHBOARD_TABS).catch('users').default('users'),
  group: z.enum(COST_BREAKDOWN_GROUPS).catch('site').default('site'),
  audience: z.enum(COST_USAGE_AUDIENCES).catch('external').default('external'),
})

/**
 * Partial patch of the search params, for the typed updateSearch writer.
 * Partial on purpose: a patch is intentionally sparse, while the schema's
 * .default() calls make the enum params required in the inferred output.
 */
export type CostDashboardSearchPatch = Partial<
  z.infer<typeof costDashboardSearchSchema>
>
